import { setAccessToken } from './access-token';
import type { SessionMember } from './router.types';

/** `auth.refresh` 응답. 리프레시 토큰은 여기 없다 — 쿠키로만 오간다. */
export interface SessionResponse {
  accessToken: string;
  member: SessionMember;
}

export type RefreshCall = () => Promise<SessionResponse>;
export type SessionRefresher = () => Promise<SessionMember>;

/**
 * 액세스 토큰 재발급기. 같은 시점에 몇 번을 불러도 서버에는 한 번만 간다.
 *
 * 리프레시 토큰은 쓸 때마다 회전한다. 두 요청이 같은 토큰으로 동시에 재발급을 시도하면
 * 뒤엣것은 이미 소비된 토큰을 들고 가고, 서버는 그것을 탈취로 보고 세션을 통째로 끊는다.
 * 그래서 진행 중인 호출이 있으면 새로 부르지 않고 그 약속을 함께 기다린다.
 *
 * 개발 모드의 StrictMode 는 부팅 효과를 두 번 실행한다 — 이 합류가 없으면
 * 앱을 열자마자 자기 세션을 끊는다.
 */
export function createSessionRefresher(refresh: RefreshCall): SessionRefresher {
  let inFlight: Promise<SessionMember> | null = null;

  return () => {
    if (inFlight !== null) {
      return inFlight;
    }

    const pending = refresh().then(
      ({ accessToken, member }) => {
        setAccessToken(accessToken);
        return member;
      },
      (error: unknown) => {
        // 재발급이 실패했다면 들고 있던 토큰도 이미 죽었다. 남겨 두면 브라우저가
        //   죽은 토큰으로 요청을 계속 보내고, 화면은 로그인 상태인 척한다.
        setAccessToken(null);
        throw error;
      },
    );

    // 성공이든 실패든 다음 호출은 새로 시작해야 한다 — 붙잡아 두면 한 번 실패한 세션이
    //   영원히 실패한 채로 남고, 성공한 토큰은 만료된 뒤에도 재발급되지 않는다.
    inFlight = pending.finally(() => {
      inFlight = null;
    });
    return inFlight;
  };
}

/**
 * 앱을 열 때 1회. 메모리에만 있던 액세스 토큰은 새로고침으로 사라지므로,
 * httpOnly 쿠키에 남은 리프레시 토큰으로 세션을 되살린다.
 *
 * 실패를 삼키는 것은 의도다 — 세션이 없는 것도, 만료된 것도, 서버가 잠시 안 되는 것도
 * 화면이 할 일은 같다(비로그인으로 시작). 로그인이 필요한 요청은 그때 각자 401 을 받는다.
 */
export async function restoreSession(refresh: SessionRefresher): Promise<SessionMember | null> {
  try {
    return await refresh();
  } catch {
    return null;
  }
}
